import React from 'react';
import { HardHat } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useConstruction } from '../../context/ConstructionContext';
import { renewableStageLabels } from '../../data/renewablesZero';
import { formatMoney } from '../../utils/helpers';
import { formatIndex, formatPct } from '../../construction/money';
import EmptyState from '../common/EmptyState';
import Badge from '../common/Badge';
import Button from '../common/Button';
import { t } from '../../i18n';

// Estilos compartidos de formularios del módulo (mismos que el resto del ERP).
export const inputClass =
  'w-full h-8 px-2.5 text-sm rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400';
export const textareaClass =
  'w-full px-2.5 py-1.5 text-sm rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400';
export const labelClass = 'block text-xs font-medium text-neutral-600 mb-1';

export const Panel = ({ title, description, actions, children, className = '' }) => (
  <section className={`bg-white border border-neutral-200 rounded ${className}`}>
    {(title || actions) && (
      <div className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-start justify-between gap-2">
        <div>
          {title && <h2 className="text-sm font-semibold text-neutral-900">{t(title)}</h2>}
          {description && <p className="text-xs text-neutral-500 mt-0.5">{t(description)}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const indexTone = (value) => {
  if (value == null) return 'neutral';
  if (value >= 1) return 'success';
  if (value >= 0.9) return 'warning';
  return 'danger';
};

export const IndexBadge = ({ label, value }) => (
  <Badge tone={indexTone(value)}>
    {label} {formatIndex(value)}
  </Badge>
);

export const FormError = ({ message }) =>
  message ? <p className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded px-2.5 py-1.5">{t(message)}</p> : null;

// Ejecuta una acción del contexto y devuelve el error como texto (las acciones
// lanzan Error cuando falla una validación o un permiso).
export const runAction = (fn, setError) => {
  try {
    setError('');
    return fn();
  } catch (error) {
    setError(error.message || 'No fue posible completar la acción solicitada.');
    return null;
  }
};

/**
 * Contenedor de las páginas de Construcción. Resuelve el proyecto activo
 * (contexto de proyecto global del ERP), aplica el gate RTB/NTP y entrega el
 * resumen calculado a la página mediante children(summary).
 */
const ConstructionShell = ({ title, description, children, requireItems = false }) => {
  const { parks, preferences, updatePreferences, currentUser } = useAppContext();
  const { projectGate, getSummary, settings, loadDemo, displayCurrency, convert } = useConstruction();
  const parkId = preferences?.projectContext?.parkId;
  const selectedPark = (parks || []).find((park) => park.id === parkId) || null;
  const canLoadDemo = ['super_user', 'project_director'].includes(currentUser?.role);
  const activationLabel = renewableStageLabels[settings.activationStage] || settings.activationStage;

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-neutral-900">{t(title)}</h1>
        <p className="text-sm text-neutral-500">{t(description)}</p>
      </div>
      {(parks || []).length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-neutral-500">{t('Proyecto')}</span>
          <select
            value={selectedPark?.id || ''}
            onChange={(event) => updatePreferences({ projectContext: { parkId: event.target.value } })}
            className="h-8 px-2.5 border border-neutral-300 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-brand-400"
          >
            {!selectedPark && <option value="">{t('Selecciona proyecto')}</option>}
            {parks.map((park) => (
              <option key={park.id} value={park.id}>
                {park.name}
                {projectGate(park.id).enabled ? ` · ${t('En construcción')}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  const demoAction = canLoadDemo ? { label: 'Cargar demo de Construcción', onClick: loadDemo } : null;

  if (!selectedPark) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          title="Construcción: no hay proyectos"
          description={t('El módulo se habilita por proyecto cuando alcanza la etapa {0}. Primero crea un proyecto y avánzalo en Desarrollo.', activationLabel)}
          checklist={['Crear proyecto en Desarrollo o Administración.', 'Configurar Área "Construcción" con su CECO.', t('Avanzar el proyecto hasta {0}.', activationLabel)]}
          primaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
          secondaryAction={demoAction}
        />
      </div>
    );
  }

  const gate = projectGate(selectedPark.id);
  if (!gate.enabled) {
    const stageLabel = renewableStageLabels[gate.stage] || gate.stage || t('sin etapa');
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          title="Construcción aún no habilitada para este proyecto"
          description={t('{0} está en etapa {1}. Construcción se habilita al alcanzar {2} (configurable en Administración).', selectedPark.name, stageLabel, activationLabel)}
          checklist={[t('Completar la etapa {0} en Desarrollo > Portafolio.', activationLabel), 'Configurar Área "Construcción" con su CECO para imputar costos.']}
          primaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
          secondaryAction={demoAction}
        />
      </div>
    );
  }

  const summary = getSummary(selectedPark.id);
  const project = summary?.evm?.project;
  const money = (amount) => formatMoney(Math.round(convert(amount, summary.currency, displayCurrency)), displayCurrency);

  return (
    <div className="space-y-4">
      {header}
      <div className="bg-white border border-neutral-200 rounded px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <HardHat className="w-4 h-4 text-neutral-500" />
          <span className="text-sm font-semibold text-neutral-900">{selectedPark.name}</span>
          <Badge tone="info">{t('En construcción')}</Badge>
        </div>
        <Metric label="% avance construcción" value={formatPct(project?.actualProgress)} />
        <Metric label="Programado" value={formatPct(project?.plannedProgress)} />
        <Metric label="% avance RTB (desarrollo)" value={formatPct(gate.renewable?.rtbProgress, 0)} />
        <Metric label="BAC" value={money(project?.bac || 0)} />
        <div className="flex gap-2">
          <IndexBadge label="SPI" value={project?.spi} />
          <IndexBadge label="CPI" value={project?.cpi} />
        </div>
        {!gate.hasConstructionArea && (
          <Badge tone="warning">{t('Falta Área "Construcción" con CECO: el costo real no se imputará')}</Badge>
        )}
      </div>
      {requireItems && summary.items.length === 0 ? (
        <EmptyState
          title="Aún no hay cronograma de obra"
          description="Crea la EDT/WBS del proyecto en Cronograma de obra para habilitar avance, EVM y materiales."
          primaryAction={{ label: 'Ir a Cronograma de obra', pageId: 'construction-schedule' }}
          secondaryAction={demoAction}
        />
      ) : (
        children(summary)
      )}
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-neutral-500">{t(label)}</p>
    <p className="text-sm font-semibold text-neutral-900 num text-left">{value}</p>
  </div>
);

export const DemoButton = () => {
  const { loadDemo } = useConstruction();
  const { currentUser } = useAppContext();
  if (!['super_user', 'project_director'].includes(currentUser?.role)) return null;
  return (
    <Button variant="secondary" size="sm" onClick={loadDemo}>
      {t('Cargar demo de Construcción')}
    </Button>
  );
};

export default ConstructionShell;
